
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  startOfMonth, 
  endOfMonth, 
  format, 
  subMonths,
  getMonth,
  getYear
} from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";

type ChefPerformance = {
  chefId: string;
  chefName: string;
  transactionCount: number;
  transactionVolume: number;
  avatarUrl?: string;
};

export const ChefPerformanceReport = () => {
  const [monthOffset, setMonthOffset] = useState(0);
  
  const startDate = startOfMonth(subMonths(new Date(), monthOffset));
  const endDate = monthOffset === 0 
    ? new Date() // For current month, use current date as end
    : endOfMonth(subMonths(new Date(), monthOffset));
  
  const monthYearLabel = format(startDate, 'MMMM yyyy');
  
  // Generate month options (current month and past 11 months)
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const date = subMonths(new Date(), i);
    return {
      value: i.toString(),
      label: format(date, 'MMMM yyyy')
    };
  });

  const { data: chefPerformance, isLoading } = useQuery({
    queryKey: ['chef-performance', startDate.toISOString(), endDate.toISOString()],
    queryFn: async () => {
      try {
        // First get all transactions within the date range
        const { data: transactions, error: transactionsError } = await supabase
          .from('transactions')
          .select(`
            id,
            chef_id,
            amount,
            created_at
          `)
          .eq('status', 'completed')
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString());
        
        if (transactionsError) throw transactionsError;
        
        if (!transactions || transactions.length === 0) {
          return [];
        }
        
        // Get chef profile information
        const chefIds = [...new Set(transactions.map(t => t.chef_id))];
        const { data: chefs, error: chefsError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, image_url')
          .in('id', chefIds);
        
        if (chefsError) throw chefsError;
        
        // Aggregate transaction data by chef
        const chefMap: Record<string, ChefPerformance> = {};
        
        transactions.forEach(transaction => {
          if (!chefMap[transaction.chef_id]) {
            const chef = chefs?.find(c => c.id === transaction.chef_id);
            chefMap[transaction.chef_id] = {
              chefId: transaction.chef_id,
              chefName: chef ? `${chef.first_name} ${chef.last_name}` : 'Unknown Chef',
              avatarUrl: chef?.image_url,
              transactionCount: 0,
              transactionVolume: 0
            };
          }
          
          chefMap[transaction.chef_id].transactionCount += 1;
          chefMap[transaction.chef_id].transactionVolume += parseFloat(transaction.amount.toString());
        });
        
        // Convert to array and sort by transaction volume (highest first)
        return Object.values(chefMap).sort(
          (a, b) => b.transactionVolume - a.transactionVolume
        );
      } catch (error) {
        console.error('Error fetching chef performance data:', error);
        return [];
      }
    }
  });

  const handleMonthChange = (value: string) => {
    setMonthOffset(parseInt(value));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <h3 className="text-lg font-medium">
          Monthly Chef Performance
        </h3>
        <Select value={monthOffset.toString()} onValueChange={handleMonthChange}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Select month" />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <Card>
        {isLoading ? (
          <div className="py-8 text-center">Loading chef performance data...</div>
        ) : chefPerformance?.length === 0 ? (
          <div className="py-8 text-center">No transaction data available for {monthYearLabel}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Chef Name</TableHead>
                <TableHead className="text-right">Transactions</TableHead>
                <TableHead className="text-right">Volume</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chefPerformance?.map(chef => (
                <TableRow key={chef.chefId}>
                  <TableCell className="font-medium">{chef.chefName}</TableCell>
                  <TableCell className="text-right">{chef.transactionCount}</TableCell>
                  <TableCell className="text-right">${chef.transactionVolume.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
      
      <div className="pt-2 text-sm text-muted-foreground">
        Showing chef performance for {monthYearLabel}
      </div>
    </div>
  );
};
